import React, { useEffect } from "react";
import CustomInput from "../../../../components/common/CustomInput/CustomInput";
import CustomFileUpload from "../../../../components/common/CustomFileUpload/CustomFileUpload";
import BodyHeader from "../../../../components/common/CommonBodyHeader";
import CustomTextArea from "../../../../components/common/CustomTextArea/CustomTextArea";
import CustomDropdown from "../../../../components/common/CustomDropdown/CustomDropdown";
import { useDispatch, useSelector } from "react-redux";
import { backdropLoadingAction } from "../../../../redux/features/helperSlice";
import ContactDetails from "./contact-details";
import {
   editContractorMasterAsync,
   fetchSingleContractorMasterAsync,
} from "../../../../redux/features/contractorMasterSlice";
import { getFleetMasterDropdownDataAsync } from "../../../../redux/features/fleetMaster";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { FormikProvider, Form, useFormik, FieldArray } from "formik";
import validation from "../../../../helpers/validation";
import * as Yup from "yup";
import AgreementDetails from "./agreement-details";

const validationSchema = Yup.object().shape({
   contractorName: validation.name("Contractor Name"),
   location: Yup.string().required("Location is required"),
   gstNumber: validation.gstNumber,
   gstDocument: validation.file("GST Document", false),
   address: Yup.string().required("Address is required"),

   accountNo: validation.accountNumber(),
   bankName: validation.name("Bank Name"),
   ifscCode: validation.ifscCode(),
   accountHolderName: validation.name("Account Holder Name"),
   bankDocument: validation.file("Bank Document", false),
   agreementDocument: validation.file("Agreement Document", false),
});

const initialValues = {
   contractorName: "",
   location: "",
   gstNumber: "",
   gstDocument: "",
   address: "",

   accountNo: "",
   bankName: "",
   ifscCode: "",
   accountHolderName: "",
   bankDocument: null,
   agreementDocument: null,
};

const ContractorMasterEdit = () => {
   const dispatch = useDispatch();
   const navigate = useNavigate();
   const params = useParams();

   const fleetMaster = useSelector((state) => state.fleetMaster);
   const { singleContractorMaster } = useSelector((state) => state.contractorMaster);

   const locationData = fleetMaster?.location?.map((item) => ({
      ...item,
      label: item?.name,
      value: item?.id,
   }));

   // Fetch single contractor master
   useEffect(() => {
      dispatch(backdropLoadingAction(true));
      dispatch(
         fetchSingleContractorMasterAsync({
            id: params.id,
            err: () => {
               dispatch(backdropLoadingAction(false));
               toast.error("Something went wrong !");
            },
            done: () => {
               dispatch(backdropLoadingAction(false));
            },
         })
      );
   }, [params.id]);

   useEffect(() => {
      dispatch(getFleetMasterDropdownDataAsync());
   }, [dispatch]);

   const formik = useFormik({
      initialValues: initialValues,
      validationSchema: validationSchema,
      onSubmit: handleUpdate,
   });

   const {
      errors,
      touched,
      values,
      getFieldProps,
      handleSubmit,
      setFieldValue,
      setValues,
   } = formik;

   // Set initial value fetch from api
   useEffect(() => {
      if (singleContractorMaster) {
         setValues({
            contractorName: singleContractorMaster?.name,
            location: singleContractorMaster?.location,
            gstNumber: singleContractorMaster?.gst_no,
            gstDocument: singleContractorMaster?.gst_document,
            address: singleContractorMaster?.address,

            accountNo: singleContractorMaster?.account_no,
            bankName: singleContractorMaster?.bank_name,
            ifscCode: singleContractorMaster?.ifsc_code,
            accountHolderName: singleContractorMaster?.account_holder_name,
            bankDocument: singleContractorMaster?.bank_document,
            agreementDocument: singleContractorMaster?.agreement_details,
         });
      }
   }, [singleContractorMaster]);

   function handleUpdate(formData) {
      const newData = new FormData();
      newData.append("name", formData.contractorName);
      newData.append("location", formData.location);
      newData.append("gst_no", formData.gstNumber);
      newData.append("address", formData.address);

      newData.append("account_no", formData.accountNo);
      newData.append("bank_name", formData.bankName);
      newData.append("ifsc_code", formData.ifscCode);
      newData.append("account_holder_name", formData.accountHolderName);

      if (typeof formData.gstDocument !== "string") {
         newData.append("gst_document", formData.gstDocument);
      }
      if (typeof formData.bankDocument !== "string") {
         newData.append("bank_document", formData.bankDocument);
      }
      if (typeof formData.agreementDocument !== "string") {
         newData.append("agreement_details", formData.agreementDocument);
      }

      newData.append("id", params?.id);
      newData.append("model_id", 9);
      newData.append("action_id", 2);
      newData.append("_method", "PATCH");

      dispatch(backdropLoadingAction(true));
      dispatch(
         editContractorMasterAsync({
            data: newData,
            err: () => {
               dispatch(backdropLoadingAction(false));
               toast.error("Something went wrong !");
            },
            done: () => {
               dispatch(backdropLoadingAction(false));
               toast.success("Updated successfully !");
               navigate("/contractor-master/view");
            },
         })
      );
   }

   return (
      <div className="p-3 shadow-lg">
         <BodyHeader title="Edit Contractor Master" />
         <FormikProvider value={formik}>
            <Form>
               <div className="row">
                  <div className="col-lg-12">
                     <div className="card card-primary">
                        <div className="card-header">
                           <h3 className="card-title">Personal Details</h3>
                        </div>
                        <div className="card-body">
                           <div className="row">
                              <div className="col-12 col-sm-12 col-md-4 col-lg-4">
                                 <CustomInput
                                    require
                                    label="Contractor Name"
                                    id="#name"
                                    name="name"
                                    placeholder="E.g contractor"
                                    {...getFieldProps(`contractorName`)}
                                    errors={
                                       Boolean(touched.contractorName) &&
                                       Boolean(errors.contractorName)
                                    }
                                    message={errors.contractorName}
                                 />
                              </div>

                              <div className="col-12 col-sm-12 col-md-4 col-lg-4">
                                 <CustomDropdown
                                    require
                                    label="Location"
                                    id="#location"
                                    name="location"
                                    placeholder="Enter Location"
                                    optionData={locationData}
                                    values={locationData?.filter?.(
                                       (item) => item.value == values.location
                                    )}
                                    onChange={(values) => {
                                       setFieldValue("location", values?.[0]?.value);
                                    }}
                                    errors={
                                       Boolean(touched.location) &&
                                       Boolean(errors.location)
                                    }
                                    message={errors.location}
                                 />
                              </div>

                              <div className="col-lg-4">
                                 <CustomInput
                                    require
                                    label="GST Number"
                                    id="gst_no"
                                    name="gst_no"
                                    placeholder="Enter GST Number"
                                    {...getFieldProps(`gstNumber`)}
                                    errors={
                                       Boolean(touched.gstNumber) &&
                                       Boolean(errors.gstNumber)
                                    }
                                    message={errors.gstNumber}
                                 />
                              </div>

                              <div className="col-lg-4">
                                 <CustomFileUpload
                                    require
                                    label="GST Document"
                                    id="gstUpload"
                                    name="gstUpload"
                                    {...getFieldProps(`gstDocument`)}
                                    onChange={(e) => {
                                       setFieldValue("gstDocument", e.target.files?.[0]);
                                    }}
                                    errors={
                                       Boolean(touched.gstDocument) &&
                                       Boolean(errors.gstDocument)
                                    }
                                    message={errors.gstDocument}
                                 />
                              </div>

                              <div className="col-12 col-sm-12 col-md-12 col-lg- mb-3">
                                 <CustomTextArea
                                    label="Address"
                                    placeholder="Enter Address"
                                    name="address"
                                    {...getFieldProps(`address`)}
                                    errors={
                                       Boolean(touched.address) && Boolean(errors.address)
                                    }
                                    message={errors.address}
                                 />
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>

               <div className="card card-primary">
                  <div className="card-header">
                     <h3 className="card-title">Bank Details</h3>
                  </div>
                  <div className="card-body">
                     <div className="row">
                        <div className="col-lg-4">
                           <CustomInput
                              require
                              label="Account Number"
                              id="account_no"
                              name="account_no"
                              placeholder="Enter Account Number"
                              {...getFieldProps(`accountNo`)}
                              errors={
                                 Boolean(touched.accountNo) && Boolean(errors.accountNo)
                              }
                              message={errors.accountNo}
                           />
                        </div>
                        <div className="col-lg-4">
                           <CustomInput
                              require
                              label="Bank name"
                              id="bank_name"
                              name="bank_name"
                              placeholder="Enter Bank Name"
                              {...getFieldProps(`bankName`)}
                              errors={
                                 Boolean(touched.bankName) && Boolean(errors.bankName)
                              }
                              message={errors.bankName}
                           />
                        </div>
                        <div className="col-lg-4">
                           <CustomInput
                              require
                              label="IFSC Code"
                              id="ifsc_code"
                              name="ifsc_code"
                              placeholder="Enter IFSC Code"
                              {...getFieldProps(`ifscCode`)}
                              errors={
                                 Boolean(touched.ifscCode) && Boolean(errors.ifscCode)
                              }
                              message={errors.ifscCode}
                           />
                        </div>

                        <div className="col-lg-4">
                           <CustomInput
                              require
                              label="Account Holder Name"
                              id="account_holder_name"
                              name="account_holder_name"
                              placeholder="Enter Account Holder name"
                              {...getFieldProps(`accountHolderName`)}
                              errors={
                                 Boolean(touched.accountHolderName) &&
                                 Boolean(errors.accountHolderName)
                              }
                              message={errors.accountHolderName}
                           />
                        </div>
                        <div className="col-lg-4">
                           <CustomFileUpload
                              require
                              label="Bank Document"
                              id="bankDetails"
                              name="bankDetails"
                              {...getFieldProps(`bankDocument`)}
                              onChange={(e) => {
                                 setFieldValue("bankDocument", e.target.files?.[0]);
                              }}
                              errors={
                                 Boolean(touched.bankDocument) &&
                                 Boolean(errors.bankDocument)
                              }
                              message={errors.bankDocument}
                           />
                        </div>
                     </div>
                  </div>
               </div>
            </Form>
         </FormikProvider>
         <div className="col-12 mt-5">
            <AgreementDetails
               data={singleContractorMaster?.contractor_agreement}
               id={params?.id}
            />
         </div>
         <div className="col-12 mt-5">
            <ContactDetails
               data={singleContractorMaster?.contact_personal_details}
               id={params?.id}
            />
         </div>
         <div className=" text-center mt-5">
            <button
               className="btn btn-success px-4 py-2"
               type="submit"
               onClick={handleSubmit}
            >
               <h5 className="mb-0 text-uppercase">Save details</h5>
            </button>
         </div>
      </div>
   );
};

export default ContractorMasterEdit;
